/**
 * @file    index.css - Global style for the portal
 *
 * @author  Alex Huctwith    <alex@distributive.network>
 * @author  Danny Akbarzadeh <danny@distributive.network>  
 * @author  Kevin Yu         <kevin@distributive.network>  
 *          
 *          Design Philosophy Guide:
 *          - Reduce developer comprehension time
 *          - Reduce the level of css-variable indirection
 *          - Give CSS variables name good semantic meaning
 *          - Use CSS variables to reduce redundancy and improve semantics
 *
 * @date    July 2022
 */

:root {
  /* ---------------------------------- Customizable Brand Variables ---------------------------------- */

  /* The filter equivalents can be generated using a tool like CSS Filter Generator: https://codepen.io/sosuke/pen/Pjoqqp */
  --primary-hue-main-filter: invert(46%) sepia(98%) saturate(377%)
    hue-rotate(107deg) brightness(93%) contrast(85%); /* primary-hue-main */

  --about-modal-header: #112e40;

  --gradient-hue-top: #0f253a;
  --gradient-hue-bottom: #1a675d;

  /* ---------------------------------- Portal Default Variables ---------------------------------- */
  --content-hue-main: #8a8a8a;
  --content-hue-light: #c4c4c4;
  --content-hue-lighter: #c4c4c480;
  --content-hue-dark: #3a3a3a;

  --content-hue-main-filter: invert(51%) sepia(36%) saturate(6%)
    hue-rotate(337deg) brightness(99%) contrast(92%); /* #8a8a8a */

  --content-hue-bg: #f5f5f5;

  --error-hue: #b83b33;

  --disabled-hue: #e5e5e5;
}

/* Hiding branded content by default */
.branded-content {
  display: none;
}

/** @todo undup portal and cdn fonts /wg sep 2025 */
@font-face {
  font-family: "Proxima Nova";
  src: url("./assets/fonts/proxima-nova.otf");
  font-weight: normal;
}

@font-face {
  font-family: "Proxima Nova";
  src: url("./assets/fonts/proxima-nova-bold.otf");
  font-weight: bold;
}

/* Berkeley Font Faces */
@font-face {
  font-family: "Berkeley";
  src: url("./assets/fonts/berkeley-mono-regular.woff");
  font-weight: normal;
}

@font-face {
  font-family: "Berkeley";
  src: url("./assets/fonts/berkeley-mono-bold-italic.woff");
  font-weight: bold;
  font-style: italic;
}

@font-face {
  font-family: "Berkeley";
  src: url("./assets/fonts/berkeley-mono-bold.woff");
  font-weight: bold;
}

@font-face {
  font-family: "Berkeley";
  src: url("./assets/fonts/berkeley-mono-italic.woff");
  font-style: italic;
}

/* Use a more-intuitive box-sizing model. */
*,
*::before,
*::after {
  box-sizing: border-box;
  scrollbar-width: thin;
}

body {
  color: var(--content-hue-main);
  margin: 0;
  font-family: "Proxima Nova", sans-serif;
  font-size: 16px;
  /**
   * Default for 16px on most systems, but allow user to override with font-scaling settings in
   * their browser.
   */
  font-size: 1rem;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Remove built-in form typography styles. */
input,
button[data-reset],
textarea,
select {
  font: inherit;
}

/* Default Button */
input[type="button"][data-reset],
button[type="button"][data-reset],
button[type="reset"][data-reset],
button[data-reset] {
  min-width: initial;
  border: none;
  background-color: transparent;
  color: inherit;
  height: initial;
  padding: initial;
}

input[type="button"][data-reset]:focus-visible,
button[type="button"][data-reset]:focus-visible,
button[type="reset"][data-reset]:focus-visible,
button[data-reset]:focus-visible {
  background-color: transparent;
}

input[type="button"][data-reset]:hover,
button[type="button"][data-reset]:hover,
button[type="reset"][data-reset]:hover,
button[data-reset]:hover {
  background-color: transparent;
}

code {
  font-family: "Berkeley", Courier, monospace;
}

@media only screen and (max-width: 480px) {
  body {
    font-size: 1em;
  }
}

@media only screen and (max-width: 375px) {
  body {
    font-size: 4vw;
  }
}

/* title */
h1 {
  font-weight: bold;
  font-size: 1.3em;
  line-height: 1.4;
  color: var(--content-hue-dark);
  margin-block-start: 0;
  margin-block-end: 0;
}

/* title-variant */
h2 {
  font-weight: bold;
  font-size: 1.3em;
  line-height: 1.4;
}


